import { useState } from "react";
import { Gauge, ScrollText, Target } from "lucide-react";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { StatCard } from "../../../components/AthenaPricingEngine/stat-card";
import { DisciplineTable } from "../../../components/AthenaPricingEngine/discipline-table";
import { TrendLineChart } from "../../../components/AthenaPricingEngine/charts/trend-line-chart";
import { GroupedBarChart } from "../../../components/AthenaPricingEngine/charts/grouped-bar-chart";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import {
  DISCIPLINE_CAPTION,
  scopeLabel,
  useDisciplineSummary,
  type DisciplineLens,
  type DisciplineRow,
  type DisciplineView,
} from "../lib/api/discipline";

/** Ranked by magnitude of deviation, largest first. */
function deviationRanking(rows: DisciplineRow[]) {
  return [...rows]
    .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation))
    .map((row) => ({ label: scopeLabel(row.scope), deviation: row.deviation }));
}

/** Ranked by commission-rate leakage, worst (largest overpay) first. */
function commissionRanking(rows: DisciplineRow[]) {
  return [...rows]
    .sort((a, b) => b.commissionRateDeviation - a.commissionRateDeviation)
    .map((row) => ({
      label: scopeLabel(row.scope),
      actualInternalCommissionRate: Number(row.actualInternalCommissionRate.toFixed(2)),
      targetInternalCommissionRate: Number(row.targetInternalCommissionRate.toFixed(2)),
    }));
}

/** "By rep" has no Office/Industry collapse to keep the row count sane (176
 * reps today), so it's capped to the top 10 by whatever "worst" means for
 * that particular display -- "By segment" stays uncapped, unchanged. */
function topByRep<T>(rows: T[], lens: DisciplineLens, compare: (a: T, b: T) => number, limit = 10): T[] {
  if (lens !== "By rep") return rows;
  return [...rows].sort(compare).slice(0, limit);
}

/** By segment/By rep + Office/Industry, top right of a ranking panel. These
 * are hierarchical, not combined: "By rep" shows the (office, rep) breakdown
 * directly and the Office/Industry sub-toggle doesn't apply to it (disabled);
 * "By segment" unlocks Office/Industry, which then picks a single collapsed
 * dimension -- one bar per office, or one bar per industry. */
function RankingControls({
  lens,
  onLensChange,
  view,
  onViewChange,
}: {
  lens: DisciplineLens;
  onLensChange: (lens: DisciplineLens) => void;
  view: DisciplineView;
  onViewChange: (view: DisciplineView) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup
        type="single"
        value={lens}
        onValueChange={(value) => value && onLensChange(value as DisciplineLens)}
        className="w-fit border border-border bg-secondary"
      >
        <ToggleGroupItem value="By segment" className="h-8 px-3 text-xs">
          By Segment
        </ToggleGroupItem>
        <ToggleGroupItem value="By rep" className="h-8 px-3 text-xs">
          By Rep
        </ToggleGroupItem>
      </ToggleGroup>
      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(value) => value && onViewChange(value as DisciplineView)}
        disabled={lens === "By rep"}
        className="w-fit border border-border bg-secondary disabled:opacity-40"
      >
        <ToggleGroupItem value="Office" className="h-8 px-3 text-xs">
          Office
        </ToggleGroupItem>
        <ToggleGroupItem value="Industry" className="h-8 px-3 text-xs">
          Industry
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

export default function Discipline() {
  // One shared lens/view: Deviation ranking, Commission leakage ranking, and
  // the Breakdown table all move together -- changing the toggle on any one
  // of them updates all three (by explicit instruction, reversing the
  // earlier per-panel-independent design).
  const [lens, setLens] = useState<DisciplineLens>("By segment");
  const [view, setView] = useState<DisciplineView>("Office");

  // Stat cards, Breakdown table, and the Adherence-by-month trend all want
  // the full (office, industry)/(office, rep) pair breakdown, never collapsed.
  const summary = useDisciplineSummary(lens);

  // Deviation ranking and Commission leakage ranking share the same
  // collapsed-or-rep data -- `deviation`/`commissionRateDeviation` are
  // numerically identical per row (see discipline.py's `_scope_row`), so one
  // query covers both charts instead of fetching the same rows twice.
  // Office/Industry only applies "By segment" -- "By rep" shows the full
  // (office, rep) breakdown directly, so no `view` is sent for it (see
  // RankingControls, which disables that toggle in "By rep" mode too).
  const rankingSummary = useDisciplineSummary(lens, lens === "By segment" ? view : undefined);
  const rankingRows = rankingSummary.data?.byScope ?? [];
  const deviationTopRows = topByRep(
    rankingRows,
    lens,
    (a, b) => Math.abs(b.deviation) - Math.abs(a.deviation),
  );
  const commissionTopRows = topByRep(
    rankingRows,
    lens,
    (a, b) => b.commissionRateDeviation - a.commissionRateDeviation,
  );

  // Breakdown table now shares the same Office/Industry-collapsed (or full
  // rep) rows as the two ranking charts above, rather than always showing the
  // full (office, industry) pair breakdown -- same `rankingRows` source. Its
  // own default sort is "deviation ascending" (most negative first, matching
  // its "Worst deviation first" description) -- the By-rep cap uses that same
  // ordering rather than |deviation|.
  const breakdownTopRows = topByRep(rankingRows, lens, (a, b) => a.deviation - b.deviation);

  return (
    <div>
      <PageHeader
        title="Discipline"
        subtitle="Recommended yield versus what was actually charged"
      />

      <p className="mt-4 text-xs text-muted-foreground">{DISCIPLINE_CAPTION}</p>

      {summary.isPending ? (
        <div className="mt-8 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Skeleton className="h-36" />
            <Skeleton className="h-36" />
            <Skeleton className="h-36" />
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      ) : summary.isError ? (
        <p
          role="alert"
          className="mt-8 border border-destructive/50 bg-card p-4 text-sm text-destructive"
        >
          Couldn't load pricing discipline. Try again.
        </p>
      ) : summary.data ? (
        <>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <StatCard
              label="Adherence Rate"
              value={`${summary.data.adherenceRate.current.toFixed(1)}%`}
              icon={Gauge}
              sparkline={summary.data.adherenceRate.sparkline}
              trend={{
                value: `${(
                  summary.data.adherenceRate.current - summary.data.adherenceRate.previousPeriod
                ).toFixed(1)}% vs. previous period`,
                direction:
                  summary.data.adherenceRate.current >= summary.data.adherenceRate.previousPeriod
                    ? "up"
                    : "down",
                goodDirection: "up",
              }}
              hint="Share of deals priced at or above their recommended yield."
            />
            <StatCard
              label="Average Deviation"
              value={`${summary.data.avgDeviation.current > 0 ? "+" : ""}${summary.data.avgDeviation.current.toFixed(2)}%`}
              icon={Target}
              trend={{
                value: `${(
                  summary.data.avgDeviation.current - summary.data.avgDeviation.previousPeriod
                ).toFixed(2)}% vs. previous period`,
                direction:
                  summary.data.avgDeviation.current >= summary.data.avgDeviation.previousPeriod
                    ? "up"
                    : "down",
                goodDirection: "up",
              }}
              hint="Deal-count weighted actual yield less target yield."
            />
            <StatCard
              label="Overrides"
              value={String(summary.data.overrideCount.current)}
              icon={ScrollText}
              sparkline={summary.data.overrideCount.sparkline}
              sparkColor="var(--decline)"
              hint="Deals priced away from the recommendation with a reason captured."
            />
          </div>

          <Panel
            className="mt-6"
            title="Deviation Ranking"
            description="Largest deviation first, one bar per office or per industry."
            right={
              <RankingControls lens={lens} onLensChange={setLens} view={view} onViewChange={setView} />
            }
          >
            <GroupedBarChart
              data={deviationRanking(deviationTopRows)}
              categoryKey="label"
              series={[{ dataKey: "deviation", name: "Deviation", color: "var(--athena-yellow)" }]}
              layout="horizontal-bars"
              categoryWidth={240}
              height={Math.max(240, deviationTopRows.length * 28 + 40)}
            />
          </Panel>

          <Panel
            className="mt-6"
            title="Commission Leakage Ranking"
            description="Internal commission rate actually paid against the approved target rate, worst overpay first."
            right={
              <RankingControls lens={lens} onLensChange={setLens} view={view} onViewChange={setView} />
            }
          >
            <GroupedBarChart
              data={commissionRanking(commissionTopRows)}
              categoryKey="label"
              series={[
                {
                  dataKey: "actualInternalCommissionRate",
                  name: "Actual Rate",
                  color: "rgb(0, 158, 224)",
                },
                {
                  dataKey: "targetInternalCommissionRate",
                  name: "Target Rate",
                  color: "var(--neutral-inactive)",
                },
              ]}
              layout="horizontal-bars"
              categoryWidth={240}
              height={Math.max(260, commissionTopRows.length * 34 + 50)}
            />
          </Panel>

          <Panel
            className="mt-6"
            title="Breakdown"
            description="Worst deviation first. Click a row for that scope's override reasons."
            right={
              <RankingControls lens={lens} onLensChange={setLens} view={view} onViewChange={setView} />
            }
          >
            <DisciplineTable rows={breakdownTopRows} />
          </Panel>

          <Panel
            className="mt-6"
            title="Adherence Rate By Month"
            description="Share of deals priced at or above recommended yield."
          >
            <TrendLineChart
              data={summary.data.trend}
              series={[
                { dataKey: "adherenceRate", name: "Adherence Rate", color: "var(--athena-yellow)" },
              ]}
            />
          </Panel>
        </>
      ) : null}
    </div>
  );
}
